import React, { useMemo, useState } from "react";
import {
    RiBuilding2Line,
    RiCalendar2Line, RiEyeLine, RiFlagLine, RiListSettingsLine, RiUserLine,
} from "@remixicon/react";
import classNames from "classnames";
import { Value } from "sass";
import SettingsContainer from "../../../../components/theme/settings";
import TableContainer from "../../../../components/theme/table";
import { parseFilterDates, Time, tryParseDate } from "../../../../date";
import { Paths, useParameterFilters } from "../../../../contexts/Routing";
import {
    AuditLogEntryFilters, AuditLogSeverity, GetAuditLogsListQuery, useGetAuditLogsListQuery,
    User,
} from "../../../../__generated__/api";
import useArticleContext from "../../../../contexts/HelpContext";
import Avatar from "../../../../components/avatar/Avatar";
import { Filters as FiltersSelector } from "../../../../components/filters/Filters";
import DateRangeCalendarPicker from "../../../../components/pickers/date-range/DateRangeCalendarPicker";
import Styles from "./AuditLogs.module.scss";
import CodeBlock from "../../../../components/code-block/CodeBlock";
import Confirm from "../../../../components/confirm/Confirm";
import UserDisplay from "../../../../components/agent-v3/labels/user/User";
import { Direction } from "../../../../components/agent-v3/board-toolbar/SortBy";

type AuditLogEntryItem = GetAuditLogsListQuery["auditLogs"][0];

const AuditLogContainer = TableContainer<AuditLogEntryItem>();

function getStartDate() {
    return new Date(new Date().getTime() - (Time.WEEK * 2));
}
type AuditLogKey = keyof AuditLogEntryItem;

const SEVERITY_TEXT = {
    [AuditLogSeverity.Trace]: "Trace",
    [AuditLogSeverity.Debug]: "Debug",
    [AuditLogSeverity.Info]: "Info",
    [AuditLogSeverity.Warn]: "Warn",
    [AuditLogSeverity.Error]: "Error",
    [AuditLogSeverity.Fatal]: "Fatal",
};
const SEVERITIES = Object.entries(SEVERITY_TEXT).map(([value, label]) => ({ value, label }));

interface Filters extends AuditLogEntryFilters {
    sortKey?: AuditLogKey;
    sortDirection?: Direction;
}

export default function AuditLogsV3() {
    useArticleContext("urn:fq:article:1ead24cac857484296a375b5400b74e3");

    const [filters, setFilter, setFilters] = useParameterFilters<Filters>(Paths.ADMINISTRATOR, {
        startDate: getStartDate(),
    });

    const { data, loading: isLoading, error } = useGetAuditLogsListQuery({
        variables: {
            filters: {
                startDate: filters.startDate,
                endDate: filters.endDate,
            },
        },
    });

    const [log, setLog] = useState<AuditLogEntryItem | undefined>(undefined);

    const actors = useMemo(
        () => data?.auditLogs
            .map(row => row.actor)
            .filterNull()
            .dedupe(item => item?.id)
            .map(actor => ({
                value: actor.id,
                label: actor.name as string,
            })),
        [data?.auditLogs],
    );

    const workspaces = useMemo(
        () => data?.auditLogs
            .map(row => row.workspace)
            .filterNull()
            .dedupe(item => item?.installationId)
            .map(item => ({
                value: item.installationId,
                label: item.name as string,
            })),
        [data?.auditLogs],
    );
    const shouldShowWorkspace = true;// workspaces != null && workspaces?.length > 1;

    return (
        <SettingsContainer navigation="none">
            <SettingsContainer.Section title="Audit Logs" shouldDisplayTitle={false}>
                <SettingsContainer.Setting
                    title="Audit Logs"
                    description="View all user adit logs"
                    permissions={SettingsContainer.Setting.Permissions.Administrator}
                    isFullWidth
                >
                    {log && (
                        <Confirm
                            shouldShow
                            size={log.differences ? "xl" : "lg"}
                            onHide={() => setLog(undefined)}
                            title={log.message.substring(0, 40)}
                        >
                            <div className="row">
                                <div className={classNames(Styles["log-header"], "col-3")}>Timestamp</div>
                                <div className="col">{tryParseDate(log.timestamp)?.toLocaleString()}</div>
                            </div>
                            <div className="row">
                                <div className={classNames(Styles["log-header"], "col-3")}>Actor</div>
                                <div className="col">
                                    <UserDisplay user={log.actor as User} allowOverlay={false} variant="xxs" />
                                </div>
                            </div>
                            <div className="row">
                                <div className={classNames(Styles["log-header"], "col-3")}>Message</div>
                                <div className="col">{log.message}</div>
                            </div>
                            <div className="row">
                                <div className={classNames(Styles["log-header"], "col-3")}>Severity</div>
                                <div className="col">{SEVERITY_TEXT[log.severity]}</div>
                            </div>
                            {shouldShowWorkspace && (
                                <div className="row">
                                    <div className={classNames(Styles["log-header"], "col-3")}>Workspace</div>
                                    <div className="col">{log.workspace?.name}</div>
                                </div>
                            )}

                            <div className="row">
                                <div className={classNames(Styles["log-header"], "col-3")}>IP Address</div>
                                <div className="col">{log.ipAddress}</div>
                            </div>

                            <div className="mt-4">
                                <h5>Properties</h5>
                                <CodeBlock code={JSON.stringify(log.attributes, null, 4)} />
                            </div>
                            {log.differences?.isNotEmpty && (
                                <div className="mt-4">
                                    <h5>Changes</h5>
                                    <table className={classNames("table w-100", Styles.table)}>
                                        <thead>
                                            <tr>
                                                <th>Path</th>
                                                <th>Previous</th>
                                                <th>Updated</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {log.differences.map(difference => (
                                                <tr key={difference.path.join(".")}>
                                                    <td>{difference.path.join(".")}</td>
                                                    <td>{difference.previousValue}</td>
                                                    <td>{difference.updatedValue}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                        </Confirm>
                    )}
                    <AuditLogContainer data={data?.auditLogs} isLoading={isLoading} error={error}>
                        <AuditLogContainer.Empty>
                            <AuditLogContainer.Empty.Default
                                text="Audit logs allow you to see activity that occurs on your account.
                                As you use the system, you will see activity populate into this list."
                                title="Audit Logs"
                                icon={<RiListSettingsLine />}
                            />
                        </AuditLogContainer.Empty>
                        <AuditLogContainer.TableControls>
                            <AuditLogContainer.TableControls.Search
                                search={(query, item) => item.message?.toLocaleLowerCase().includes(query)
                                                    || item.severity?.toLocaleLowerCase().includes(query)
                                                    || item.actor?.name?.toLocaleLowerCase().includes(query)}
                            />
                            <AuditLogContainer.TableControls.Seperator />

                            <AuditLogContainer.TableControls.Filter>
                                <DateRangeCalendarPicker
                                    toggleText="Created"
                                    icon={<RiCalendar2Line />}
                                    value={{
                                        startDate: filters.startDate,
                                        endDate: filters.endDate,
                                    }}
                                    onChange={change => {
                                        const dates = parseFilterDates(change);
                                        setFilters({
                                            ...filters,
                                            startDate: dates.startDate ? new Date(dates.startDate) : undefined,
                                            endDate: dates.endDate ? new Date(dates.endDate) : undefined,
                                        });
                                    }}
                                />
                            </AuditLogContainer.TableControls.Filter>
                            <AuditLogContainer.TableControls.Filter
                                defaultValue={new Set<string>()}
                                deserialize={AuditLogContainer.TableControls.Filter.Serializers.deserializeSet}
                                serialize={AuditLogContainer.TableControls.Filter.Serializers.serializeSet}
                                filterKey="actorIds"
                                onFilter={(item, selected) => selected.has(item.actor?.id)}
                            >
                                {(filter, setActors) => (
                                    <FiltersSelector
                                        toggleText="Actor"
                                        className=""
                                        icon={<RiUserLine />}
                                        categories={actors ?? []}
                                        searchMinimum={5}
                                        selectedCategories={filter}
                                        onSelected={setActors}
                                    />
                                )}

                            </AuditLogContainer.TableControls.Filter>
                            <AuditLogContainer.TableControls.Filter
                                defaultValue={new Set<string>()}
                                deserialize={AuditLogContainer.TableControls.Filter.Serializers.deserializeSet}
                                serialize={AuditLogContainer.TableControls.Filter.Serializers.serializeSet}
                                filterKey="severities"
                                onFilter={(item, selected) => selected.has(item.severity)}
                            >
                                {(severities, setSeverities) => (
                                    <FiltersSelector
                                        toggleText="Severity"
                                        className=""
                                        icon={<RiFlagLine />}
                                        categories={SEVERITIES}
                                        selectedCategories={severities}
                                        onSelected={setSeverities}
                                    />
                                )}
                            </AuditLogContainer.TableControls.Filter>
                            {shouldShowWorkspace && (
                                <AuditLogContainer.TableControls.Filter
                                    defaultValue={new Set<string>()}
                                    deserialize={AuditLogContainer.TableControls.Filter.Serializers.deserializeSet}
                                    serialize={AuditLogContainer.TableControls.Filter.Serializers.serializeSet}
                                    filterKey="workspaceIds"
                                    onFilter={(item, selected) => selected.has(item.workspace?.installationId)}
                                >
                                    {(workspaceIds, setWorkspaceIds) => (
                                        <FiltersSelector
                                            toggleText="Workspace"
                                            categories={workspaces}
                                            icon={<RiBuilding2Line />}
                                            searchMinimum={5}
                                            selectedCategories={workspaceIds}
                                            onSelected={setWorkspaceIds}
                                        />
                                    )}
                                </AuditLogContainer.TableControls.Filter>
                            )}
                            <AuditLogContainer.TableControls.Filters />
                            <AuditLogContainer.TableControls.SortBy />
                        </AuditLogContainer.TableControls>
                        <AuditLogContainer.Table>
                            <AuditLogContainer.TableValue
                                columnKey="timestamp"
                                title="Timestamp"
                                sortBy={item => tryParseDate(item.timestamp)?.getTime()}
                            >
                                {item => tryParseDate(item.timestamp)?.toLocaleString()}
                            </AuditLogContainer.TableValue>
                            <AuditLogContainer.TableValue
                                columnKey="action"
                                title="Action"
                                sortBy={item => item.message}
                            >
                                {item => item.message}
                            </AuditLogContainer.TableValue>
                            <AuditLogContainer.TableValue
                                columnKey="actor"
                                title="Actor"
                                sortBy={item => item.actor?.name}
                            >
                                {item => (
                                    <>
                                        <Avatar src={item.actor?.avatarUrl as string} variant="sm-2" />
                                        {item.actor?.name}
                                    </>
                                )}
                            </AuditLogContainer.TableValue>
                            <AuditLogContainer.TableValue
                                columnKey="severity"
                                title="Severity"
                                sortBy={item => item.severity}
                            >
                                {item => SEVERITY_TEXT[item.severity]}
                            </AuditLogContainer.TableValue>
                            <AuditLogContainer.Action
                                icon={<RiEyeLine />}
                                altText="View Audit Log"
                                onClick={selected => {
                                    setLog(selected);
                                }}
                            />

                        </AuditLogContainer.Table>
                    </AuditLogContainer>
                </SettingsContainer.Setting>
            </SettingsContainer.Section>

        </SettingsContainer>
    );
}
